// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import PanelError from '@wandb/weave/common/components/elements/PanelError';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from '@wandb/weave/common/components/elements/Toast';
import {TargetBlank} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {clsx} from 'clsx';
import _ from 'lodash';
import React, {useCallback, useEffect, useMemo} from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';

import {toRunsDataQueryConfig} from '../../containers/RunsDataLoader';
import {ParameterImportanceQueryVariables} from '../../generated/graphql';
import {
  useLazyParameterImportanceQuery,
  useParameterImportanceQuery,
} from '../../state/graphql/paramImportanceQuery';
import DOC_URLS from '../../util/doc_urls';
import {makeFetchProjectFieldOptions} from '../../util/dropdownQueries';
import * as Filter from '../../util/filters';
import {PanelProps} from '../../util/panelProps';
import {ConfigV2, EMPTY_CONFIG} from '../../util/runfeed';
import {
  fixConfigKeyString,
  isReservedKey,
  keyStringDisplayName,
} from '../../util/runs';
import {getIconNameForKey} from '../../util/uihelpers';
import {TruncateTextTooltip} from '../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {ModalTriggerButton} from '../elements/ModalTriggerButton';
import {WBSearchableSelect} from '../elements/WBSearchableSelect';
import {useStablePanelId} from '../Panel/PanelIDContext';
import ParameterImportanceTableEditor from '../RunSelector/TableEditor/ParameterImportanceTableEditor';
import {InstrumentedLoader as Loader} from '../utility/InstrumentedLoader';
import WBReactTable from '../WBReactTable';
import {ParameterImportanceConfig} from './common';

type PanelParameterImportanceProps = PanelProps<ParameterImportanceConfig>;

const AutoParametersButton: React.FC<{
  queryVariables: ParameterImportanceQueryVariables;
  parameterConf: ConfigV2;
  setParameterConf: (newConf: ConfigV2) => void;
}> = ({queryVariables, parameterConf, setParameterConf}) => {
  const [fetchParameterImportances, {loading, data}] =
    useLazyParameterImportanceQuery(queryVariables);
  const [buttonClicked, setButtonClicked] = React.useState(false);

  useEffect(() => {
    if (!loading && data != null && buttonClicked) {
      const importanceData = data.project.parameterImportance;
      // Filter out rows where importance and correlation are 0
      const filteredImportanceData = _.pickBy(importanceData, v => {
        return v.importance !== 0 && v.correlation !== 0;
      });
      const autoParameters = Object.keys(filteredImportanceData).map(
        p => `config:${p}`
      );
      if (!_.isEqual(autoParameters, parameterConf.columnOrder)) {
        setParameterConf({
          ...parameterConf,
          columnOrder: autoParameters,
          columnVisible: autoParameters.reduce(
            (obj, k) => ({...obj, [k]: true}),
            {}
          ),
        });
        toast('Now showing recommended parameters.');
      }
      setButtonClicked(false);
    }
  }, [loading, data, parameterConf, setParameterConf, buttonClicked]);

  return (
    <Popup
      content={'Automatically show the most useful parameters.'}
      position="top right"
      popperModifiers={{
        preventOverflow: {
          // prevent popper from erroneously constraining the popup to the panel
          boundariesElement: 'viewport',
        },
      }}
      trigger={
        <Button
          disabled={loading}
          icon="magic-wand-star"
          variant="secondary"
          onClick={() => {
            fetchParameterImportances();
            setButtonClicked(true);
          }}
        />
      }
    />
  );
};

export default function PanelParameterImportance(
  props: PanelParameterImportanceProps
) {
  const {updateConfig} = props;
  // targetKey is the comparison metric
  const targetKey = props.config.targetKey || '';
  const setTargetKey = useCallback(
    (key: string | number | undefined) =>
      updateConfig({
        targetKey: key == null ? undefined : key.toString().split(/:(.+)/)[1],
      }),
    [updateConfig]
  );
  // parameterConf stores which parameters are visible
  const parameterConf = props.config.parameterConf || null;
  const setParameterConf = useCallback(
    (newConf: ConfigV2) => updateConfig({parameterConf: newConf}),
    [updateConfig]
  );
  const allConfigKeys = Object.keys(props.data.keyInfo).filter(
    k => k.startsWith('config:') && !isReservedKey(k.split(':')[1])
  );
  // Runtime isn't a config, but we should let users see how it correlates to target metrics.
  allConfigKeys.push('run:duration');

  useEffect(() => {
    if (!props.data.loading) {
      let params: string[] = [];
      // if parameterConf is empty, initialize it with all config keys
      if (parameterConf == null) {
        params = allConfigKeys;
      } else if (
        // if any parameterConf keys aren't formatted correctly, fix them
        parameterConf.columnOrder.some(
          k => !k.startsWith('config:') && !k.startsWith('run:')
        )
      ) {
        params = parameterConf.columnOrder.map(k =>
          k.startsWith('config:') ? k : fixConfigKeyString(`config:${k}`)
        );
      }
      if (params.length > 0) {
        setParameterConf({
          ...EMPTY_CONFIG,
          // Set all config params to be visible by default
          columnVisible: params.reduce((obj, k) => ({...obj, [k]: true}), {}),
          columnOrder: params,
        });
      }
    }
  }, [props.data.loading, allConfigKeys, parameterConf, setParameterConf]);

  // Use toRunsDataQuery to get the filters for each run set.
  const rdq = toRunsDataQueryConfig(
    props.pageQuery,
    {selectionsAsFilters: true},
    {panelType: 'Parameter Importance'}
  );
  const importanceFilters = Filter.Or(rdq.runsetQueries.map(q => q.filters));

  const runLimit = 1000;
  const parameters = parameterConf?.columnOrder.filter(c =>
    c.startsWith('config:')
  );
  const runParameters = parameterConf?.columnOrder.filter(c =>
    c.startsWith('run:')
  );
  const parameterImportanceQueryVariables = {
    projectName:
      props.query.runsetQueries[0]?.projectName ?? props.query.projectName,
    entityName:
      props.query.runsetQueries[0]?.entityName ?? props.query.entityName,
    targetName: targetKey,
    limit: runLimit,
    parameters,
    runParameters,
    filters: JSON.stringify(Filter.toMongo(importanceFilters)),
  };

  const importanceData = useParameterImportanceQuery(
    parameterImportanceQueryVariables
  );

  const importances = useMemo(() => {
    if (importanceData.loading || parameterConf == null) {
      return [];
    }
    return parameterConf.columnOrder
      .flatMap(key => {
        const hp = key.split(/:(.+)/)[1];
        const allImportances =
          importanceData.data?.project.parameterImportance || {};
        const parameterImportance = allImportances[hp];
        if (parameterImportance == null) {
          // Sometimes parameter importance calls return extra keys because of the way we handle string hparams.
          // Look for those here.
          const keys = Object.keys(allImportances).filter(k =>
            k.startsWith(hp)
          );
          return keys.map(k => ({
            key: k,
            ...allImportances[k],
          }));
        } else {
          return [
            {
              key,
              ...parameterImportance,
            },
          ];
        }
      })
      .filter(row => row.importance != null && row.correlation != null);
  }, [importanceData, parameterConf]);

  const fetchProjectFieldOptions = useMemo(
    () =>
      makeFetchProjectFieldOptions(
        props.pageQuery.entityName,
        props.pageQuery.projectName,
        {columns: ['summary_metrics']}
      ),
    [props.pageQuery.entityName, props.pageQuery.projectName]
  );

  const stablePanelId = useStablePanelId();

  if (parameterConf == null || props.data.loading) {
    return (
      <div style={{position: 'relative', height: '100%'}}>
        <Loader active name="panel-parameter-importance-loader" />
      </div>
    );
  }
  if (props.data.filtered.length < 2) {
    return (
      <PanelError
        panelID={stablePanelId}
        panelViewType="Parameter Importance"
        message="Select at least two runs to visualize parameter importance."
      />
    );
  }

  // If target key is not set, use first summary metric.
  if (!targetKey && props.data.keyInfo) {
    for (const key of Object.keys(props.data.keyInfo)) {
      if (key.startsWith('summary:')) {
        setTargetKey(key);
        break;
      }
    }
  }

  return (
    <div
      className="panel-parameter-importance"
      data-test="panel-parameter-importance">
      <h4 className="target-selection-header">
        <div style={{flexGrow: 1}}>
          <span className="panel-title">
            Parameter importance with respect to
          </span>
          <WBSearchableSelect
            infiniteScroll
            options={fetchProjectFieldOptions}
            value={targetKey ? 'summary:' + targetKey : ''}
            initialOption={
              targetKey
                ? {
                    icon: getIconNameForKey({
                      section: 'summary',
                      name: targetKey,
                    }),
                    value: 'summary:' + targetKey,
                    name: targetKey,
                  }
                : undefined
            }
            fetchInitialValue
            onSelect={setTargetKey}
          />
        </div>
        {props.data.totalRuns > runLimit && (
          <div className="message">Limited to {runLimit} runs</div>
        )}
      </h4>
      <WBReactTable
        loading={importanceData.loading}
        sortable={true}
        pageSize={10}
        defaultSorted={[{id: 'importance', desc: true}]}
        extraActions={
          <div className="mr-8 flex flex-row gap-8">
            <ModalTriggerButton
              buttonIcon="settings"
              buttonText="Parameters"
              modalHeader="Manage Parameters"
              modalContent={
                <ParameterImportanceTableEditor
                  allColumns={allConfigKeys}
                  config={parameterConf}
                  update={setParameterConf}
                  objectType="Parameters"
                />
              }
            />
            <AutoParametersButton
              queryVariables={{
                ...parameterImportanceQueryVariables,
                parameters: allConfigKeys,
              }}
              parameterConf={parameterConf}
              setParameterConf={setParameterConf}
            />
          </div>
        }
        columns={[
          {
            Header: 'Config parameter',
            accessor: 'key',
            Cell: row => {
              const keyDisplayName = keyStringDisplayName(row.row.key);
              return (
                <TruncateTextTooltip
                  truncationType={TruncationType.Middle}
                  className="text-middle"
                  fullText={keyStringDisplayName(keyDisplayName)}
                  noLink={true}
                />
              );
            },
          },
          {
            Header: (
              <>
                Importance
                <TargetBlank
                  className="docs-link"
                  href={DOC_URLS.parameterImportance}
                  onClick={(e: any) => {
                    e.stopPropagation();
                  }}>
                  <LegacyWBIcon title="" name="info" />
                </TargetBlank>
              </>
            ),
            accessor: 'importance',
            Cell: row => (
              <Popup
                inverted
                size="mini"
                position="top center"
                trigger={
                  <div
                    className={clsx('bar-wrapper', 'importance-bar-wrapper', {
                      zero: row.row.importance === 0,
                    })}>
                    <div
                      className="bar"
                      style={{width: `${row.row.importance * 100}%`}}
                    />
                  </div>
                }>
                {row.row.importance.toFixed(3)}
              </Popup>
            ),
            defaultSortDesc: true,
          },
          {
            Header: 'Correlation',
            accessor: 'correlation',
            Cell: row => {
              return (
                <Popup
                  inverted
                  size="mini"
                  position="top center"
                  trigger={
                    <div
                      className={clsx(
                        'bar-wrapper',
                        'correlation-bar-wrapper',
                        {
                          negative: row.row.correlation < 0,
                          zero: row.row.correlation === 0,
                        }
                      )}>
                      <div
                        className="bar"
                        style={{
                          width: `${Math.abs(row.row.correlation * 100)}%`,
                        }}
                      />
                    </div>
                  }>
                  {row.row.correlation.toFixed(3)}
                </Popup>
              );
            },
            id: 'correlation',
            defaultSortDesc: true,
          },
        ]}
        data={importances.map(row => {
          return {
            searchString: row.key,
            row,
          };
        })}
      />
    </div>
  );
}
